import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useKycWorkflowKycSetting } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useKycSetting.ts';
import { updateKycWorkflowKycSetting } from '@dealstack/frontend/shared/dataExchange/services/kyc-setting.service.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { UpdateKycWorkflowSettingDto } from '@dealstack/shared/lib/generated';
import { IndividualSettings, VehicleSettings } from './types.ts';

export const useInvestmentWorkflowInvestorData = (kycWorkflowId: string | undefined) => {
	const tMipManage = useTranslationMipManage();
	const queryClient = useQueryClient();

	const {
		data: kycWorkflowSettings,
		isLoading: isLoadingSettings,
		refetch: refetchKycWorkflowSettings,
	} = useKycWorkflowKycSetting(kycWorkflowId ?? '');

	const updateMutation = useMutation({
		mutationFn: (data: UpdateKycWorkflowSettingDto) =>
			updateKycWorkflowKycSetting(kycWorkflowId!, data),
		onSuccess: async (_response, variables) => {
			queryClient.setQueryData(
				[API_PATHS.KYC_SETTING_KYCWORKFLOW_KYCWORKFLOWID_GET, kycWorkflowId],
				(oldData: { payload: typeof kycWorkflowSettings } | undefined) => {
					if (!oldData?.payload) {
						return oldData;
					}
					return {
						...oldData,
						payload: { ...oldData.payload, ...variables },
					};
				}
			);
			await queryClient.invalidateQueries({
				queryKey: [API_PATHS.MIP_KYC_WORKFLOWS_GET],
			});
			showToastV2({ title: tMipManage('investor_data.fields.updated_success') });
		},
		onError: () => {
			showToastV2({
				state: 'error',
				title: tMipManage('investor_data.fields.updated_error'),
			});
		},
	});

	const individualSettings = useMemo<IndividualSettings>(
		() => ({
			bankAccountDetails: {
				checked: kycWorkflowSettings?.isInvestorBankAccountRequired ?? false,
				disabled: false,
			},
			taxResidence: {
				checked: kycWorkflowSettings?.isTaxResidenceRequired ?? false,
				disabled: false,
			},
			taxIdentificationNumber: {
				checked: kycWorkflowSettings?.isTinRequired ?? false,
				disabled: kycWorkflowSettings?.isTaxResidenceRequired !== true,
			},
			placeOfBirth: {
				checked: kycWorkflowSettings?.isPlaceOfBirthRequired ?? false,
				disabled: false,
			},
			countryOfBirth: {
				checked: kycWorkflowSettings?.isCountryOfBirthRequired ?? false,
				disabled: false,
			},
			employeeId: {
				checked: kycWorkflowSettings?.isEmployeeIdRequired ?? false,
				disabled: false,
			},
			citizenship: {
				checked: kycWorkflowSettings?.isCitizenshipRequired ?? false,
				disabled: false,
			},
		}),
		[kycWorkflowSettings]
	);

	const vehicleSettings = useMemo<VehicleSettings>(
		() => ({
			bankAccountDetails: {
				checked: kycWorkflowSettings?.isVehicleBankAccountRequired ?? false,
				disabled: false,
			},
			controllingPartyInfo: {
				checked: kycWorkflowSettings?.isControllingPartyInfoRequired ?? false,
				disabled: false,
			},
		}),
		[kycWorkflowSettings]
	);

	const toggleIndividualSetting = (settingKey: keyof IndividualSettings) => {
		if (!kycWorkflowId || updateMutation.isPending) {
			return;
		}

		const newValue = !individualSettings[settingKey].checked;

		const fieldMap: Record<keyof IndividualSettings, keyof UpdateKycWorkflowSettingDto> = {
			bankAccountDetails: 'isInvestorBankAccountRequired',
			taxResidence: 'isTaxResidenceRequired',
			taxIdentificationNumber: 'isTinRequired',
			placeOfBirth: 'isPlaceOfBirthRequired',
			countryOfBirth: 'isCountryOfBirthRequired',
			employeeId: 'isEmployeeIdRequired',
			citizenship: 'isCitizenshipRequired',
		};

		if (settingKey === 'taxResidence' && !newValue) {
			updateMutation.mutate({
				[fieldMap[settingKey]]: newValue,
				isTinRequired: false,
			});
		} else {
			updateMutation.mutate({ [fieldMap[settingKey]]: newValue });
		}
	};

	const toggleVehicleSetting = (settingKey: keyof VehicleSettings) => {
		if (!kycWorkflowId || updateMutation.isPending) {
			return;
		}

		const newValue = !vehicleSettings[settingKey].checked;

		const fieldMap: Record<keyof VehicleSettings, keyof UpdateKycWorkflowSettingDto> = {
			bankAccountDetails: 'isVehicleBankAccountRequired',
			controllingPartyInfo: 'isControllingPartyInfoRequired',
		};

		updateMutation.mutate({ [fieldMap[settingKey]]: newValue });
	};

	return {
		individualSettings,
		vehicleSettings,
		toggleIndividualSetting,
		toggleVehicleSetting,
		isLoading: isLoadingSettings,
		refetch: refetchKycWorkflowSettings,
	};
};
